import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  OutlinedInput, 
  Box, 
  Chip 
} from '@mui/material';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { BUSINESS_AREA_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = BUSINESS_AREA_RETURN_TYPES;

const BusinessAreaSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  label = "Business Area",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = BUSINESS_AREA_RETURN_TYPES.ID, // BUSINESS_AREA_RETURN_TYPES.ID or BUSINESS_AREA_RETURN_TYPES.NAME
  size = 'medium',
  ...props 
}) => {
  const [businessAreas, setBusinessAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchBusinessAreas = async () => {
      try {
        setIsLoading(true);
        const businessAreasData = await organizationApi.getBusinessAreas();
        setBusinessAreas(businessAreasData || []);
      } catch (error) {
        showAlert('Failed to load business areas', 'error', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinessAreas();
  }, [showAlert]);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (onChange) {
      if (multiple) {
        if (returnValue === BUSINESS_AREA_RETURN_TYPES.NAME) {
          const selectedNames = selectedValue.map(id => {
            const businessArea = businessAreas.find(area => area.id === id);
            return businessArea?.name || '';
          }).filter(Boolean);
          onChange(selectedNames);
        } else {
          onChange(selectedValue);
        }
      } else {
        if (returnValue === BUSINESS_AREA_RETURN_TYPES.NAME) {
          const businessArea = businessAreas.find(area => area.id === selectedValue);
          onChange(businessArea?.name || '');
        } else {
          onChange(selectedValue || '');
        }
      }
    }
  };

  // Find the selected business area(s) for value display
  const selectedValue = (() => {
    if (!value) return multiple ? [] : '';
    
    if (multiple) {
      if (returnValue === BUSINESS_AREA_RETURN_TYPES.NAME) {
        return value.map(name => {
          const businessArea = businessAreas.find(area => area.name === name);
          return businessArea?.id;
        }).filter(Boolean);
      } else {
        return Array.isArray(value) ? value : [];
      }
    } else {
      if (returnValue === BUSINESS_AREA_RETURN_TYPES.NAME) {
        const businessArea = businessAreas.find(area => area.name === value);
        return businessArea?.id || '';
      } else {
        return value;
      }
    }
  })();

  const renderValue = (selected) => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return <em>None</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {
            const area = businessAreas.find(ba => ba.id === value);
            return (
              <Chip 
                key={value} 
                label={area?.name} 
                size="small"
              />
            );
          })}
        </Box>
      );
    }
    return undefined; // Let Material-UI handle single select rendering
  };

  return (
    <FormControl fullWidth={fullWidth} disabled={disabled || isLoading} size={size} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selectedValue}
        onChange={handleChange}
        label={label}
        disabled={disabled || isLoading}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={multiple ? renderValue : undefined}
        endAdornment={isLoading ? <CircularProgress size={20} /> : null}
      >
        {!required && !multiple && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {businessAreas.map(area => (
          <MenuItem key={area.id} value={area.id}>
            {area.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default BusinessAreaSelector; 